import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Separator } from '@/shared/components/ui/separator';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/color-picker.index';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useTags, useCreateTag, useUpdateTag, useDeleteTag } from '@/features/tags/public';
import { getColorValue } from '@/core/lib/color-contrast';
import type { Tag } from '@/features/tags/public';

const TAGS_PAGE_SIZE = 20;

// ── Internal: simple badge for trigger display (no actions) ──────────────────

function TagBadge({ tag }: { tag: Tag }) {
  const bg = getColorValue(tag.color ?? '');
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: bg ? `${bg}20` : undefined,
        borderColor: bg ? `${bg}38` : undefined,
        color: bg ?? undefined,
      }}
    >
      <span
        className="size-1.5 shrink-0 rounded-full"
        style={{ background: bg ?? 'currentColor' }}
      />
      {tag.name}
    </span>
  );
}

// ── Internal: single row in the "All tags" section ───────────────────────────

interface TagOptionRowProps {
  tag: Tag;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function TagOptionRow({ tag, onToggle, onEdit, onDelete }: TagOptionRowProps) {
  const { t } = useTranslation('tasks');
  const [hovered, setHovered] = useState(false);
  const bg = getColorValue(tag.color ?? '');

  return (
    <div
      data-testid={`tasks-tag-picker-all-item-${tag.id}`}
      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onToggle}
    >
      <span
        className="size-2 shrink-0 rounded-full"
        style={{ background: bg ?? 'hsl(var(--muted-foreground))' }}
      />
      <span className="flex-1 text-xs">{tag.name}</span>
      {hovered && (
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                data-testid={`tasks-tag-picker-item-edit-${tag.id}`}
                onClick={onEdit}
                aria-label={t('tagsInline.editAria')}
              >
                <Pencil className="size-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('tagsInline.editAria')}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 hover:text-destructive"
                data-testid={`tasks-tag-picker-item-delete-${tag.id}`}
                onClick={onDelete}
                aria-label={t('tagsInline.deleteAria')}
              >
                <Trash2 className="size-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('tagsInline.deleteAria')}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

// ── Internal: inline edit form ────────────────────────────────────────────────

interface TagEditRowProps {
  tag: Tag;
  onSave: (name: string, color: string) => void;
  onCancel: () => void;
}

function TagEditRow({ tag, onSave, onCancel }: TagEditRowProps) {
  const { t } = useTranslation('tasks');
  const [name, setName] = useState(tag.name ?? '');
  const [color, setColor] = useState(tag.color ?? '');

  return (
    <div className="rounded border bg-muted/30 px-2.5 py-2 mx-1 my-0.5">
      <div className="flex items-center gap-2.5">
        <ColorPickerPopover
          value={color}
          onSelect={setColor}
          trigger={
            <button
              type="button"
              data-testid="tasks-tag-picker-edit-color"
              className="size-5 shrink-0 rounded-full border border-border transition-opacity hover:opacity-70"
              style={{ background: getColorValue(color) ?? 'hsl(var(--muted-foreground))' }}
            />
          }
        />
        <Input
          autoFocus
          data-testid="tasks-tag-picker-edit-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && name.trim()) onSave(name.trim(), color);
            if (e.key === 'Escape') onCancel();
          }}
          placeholder={t('tagsInline.namePlaceholder')}
          className="h-6 flex-1 text-xs"
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              data-testid="tasks-tag-picker-edit-save"
              onClick={() => name.trim() && onSave(name.trim(), color)}
              disabled={!name.trim()}
              aria-label={t('tagsInline.saveAria')}
            >
              <Check className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('tagsInline.saveAria')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              data-testid="tasks-tag-picker-edit-cancel"
              onClick={onCancel}
              aria-label={t('tagsInline.cancelAria')}
            >
              <X className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('tagsInline.cancelAria')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Internal: create form at popover bottom ───────────────────────────────────

interface TagCreateFormProps {
  onSave: (name: string, color: string) => void;
  onCancel: () => void;
}

function TagCreateForm({ onSave, onCancel }: TagCreateFormProps) {
  const { t } = useTranslation('tasks');
  const [name, setName] = useState('');
  const [color, setColor] = useState('indigo-violet');

  return (
    <div className="border-t px-2.5 pt-2.5 pb-2.5">
      <div className="flex items-center gap-2.5 mb-2.5">
        <ColorPickerPopover
          value={color}
          onSelect={setColor}
          trigger={
            <button
              type="button"
              data-testid="tasks-tag-picker-create-color"
              className="size-5 shrink-0 rounded-full border border-border transition-opacity hover:opacity-70"
              style={{ background: getColorValue(color) ?? 'hsl(var(--muted-foreground))' }}
            />
          }
        />
        <Input
          autoFocus
          data-testid="tasks-tag-picker-create-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && name.trim()) onSave(name.trim(), color);
            if (e.key === 'Escape') onCancel();
          }}
          placeholder={t('tagsInline.namePlaceholder')}
          className="h-7 flex-1 text-xs"
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 flex-1 text-xs"
          data-testid="tasks-tag-picker-create-cancel"
          onClick={onCancel}
        >
          {t('tagsInline.cancelAria')}
        </Button>
        <Button
          size="sm"
          className="h-7 flex-1 text-xs"
          data-testid="tasks-tag-picker-create-save"
          onClick={() => name.trim() && onSave(name.trim(), color)}
          disabled={!name.trim()}
        >
          {t('tagsInline.createNew')}
        </Button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface TagPickerProps {
  projectId: string;
  selectedTags: Tag[];
  onChange: (tags: Tag[]) => void;
  variant?: 'compact' | 'expanded';
  disabled?: boolean;
}

export function TagPicker({
  projectId,
  selectedTags,
  onChange,
  variant = 'expanded',
  disabled,
}: TagPickerProps) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageSize, setPageSize] = useState(TAGS_PAGE_SIZE);
  const [lastSearch, setLastSearch] = useState(debouncedSearch);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // A new search term invalidates how many results are "loaded so far" — restart
  // from the first page instead of keeping whatever page size scrolling had grown to.
  // Reset during render (not an effect) per the "adjusting state on prop change" pattern.
  if (debouncedSearch !== lastSearch) {
    setLastSearch(debouncedSearch);
    setPageSize(TAGS_PAGE_SIZE);
  }

  const { data, isFetching } = useTags(projectId, { searchTerm: debouncedSearch, pageSize });
  const allProjectTags = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasMore = allProjectTags.length < totalCount;
  const selectedIds = new Set(selectedTags.map((t) => t.id));
  const unselectedTags = allProjectTags.filter(
    (tag) => tag.id !== null && !selectedIds.has(tag.id),
  );

  function handleListScroll(e: React.UIEvent<HTMLDivElement>) {
    if (!hasMore || isFetching) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      setPageSize((n) => n + TAGS_PAGE_SIZE);
    }
  }

  const createTag = useCreateTag(projectId);
  const updateTag = useUpdateTag(projectId);
  const deleteTag = useDeleteTag(projectId);
  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  function handleToggleAdd(tag: Tag) {
    onChange([...selectedTags, tag]);
  }

  function handleToggleRemove(tagId: string) {
    onChange(selectedTags.filter((t) => t.id !== tagId));
  }

  async function handleDelete(tag: Tag) {
    const { confirmed } = await confirm({
      title: t('tagsInline.confirmDeleteTitle'),
      description: t('tagsInline.confirmDeleteDesc'),
      confirmLabel: t('tagsInline.confirmDeleteConfirm'),
      cancelLabel: t('tagsInline.confirmDeleteCancel'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    handleToggleRemove(tag.id ?? '');
    deleteTag.mutate(tag.id ?? '');
  }

  async function handleCreate(name: string, color: string) {
    await createTag.mutateAsync({ name, color });
    setCreating(false);
  }

  function handleUpdate(tagId: string, name: string, color: string) {
    updateTag.mutate({ tagId, payload: { name, color } });
    setEditingTagId(null);
  }

  function handleOpenChange(v: boolean) {
    setOpen(v);
    if (!v) {
      setSearch('');
      setEditingTagId(null);
      setCreating(false);
    }
  }

  const popoverContent = (
    <PopoverContent className="w-64 p-0" align="start" data-testid="tasks-tag-picker-popover">
      <div className="px-3 py-2 text-xs font-semibold">{t('tagsInline.popoverTitle')}</div>
      <Separator />

      <div className="px-2 pt-2 pb-1">
        <Input
          data-testid="tasks-tag-picker-search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setEditingTagId(null);
          }}
          placeholder={t('tagsInline.searchPlaceholder')}
          className="h-7 text-xs"
        />
      </div>

      {selectedTags.length > 0 && (
        <>
          <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('tagsInline.activeTitle')}
          </div>
          <div data-testid="tasks-tag-picker-active-section" className="px-1 pb-1">
            {selectedTags.map((tag) => {
              const bg = getColorValue(tag.color ?? '');
              return (
                <div
                  key={tag.id}
                  data-testid={`tasks-tag-picker-active-item-${tag.id}`}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
                  onClick={() => handleToggleRemove(tag.id ?? '')}
                >
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ background: bg ?? 'hsl(var(--muted-foreground))' }}
                  />
                  <span className="text-xs">{tag.name}</span>
                </div>
              );
            })}
          </div>
          <Separator />
        </>
      )}

      <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {t('tagsInline.allTitle')}
      </div>
      <div
        data-testid="tasks-tag-picker-all-section"
        className="max-h-48 overflow-y-auto px-1 pb-1"
        onScroll={handleListScroll}
      >
        {unselectedTags.length === 0 && !editingTagId && (
          <p className="py-3 text-center text-xs text-muted-foreground">
            {search ? t('tagsInline.noResults') : t('tagsInline.empty')}
          </p>
        )}
        {unselectedTags.map((tag) =>
          editingTagId === tag.id ? (
            <TagEditRow
              key={tag.id}
              tag={tag as Tag}
              onSave={(name, color) => handleUpdate(tag.id ?? '', name, color)}
              onCancel={() => setEditingTagId(null)}
            />
          ) : (
            <TagOptionRow
              key={tag.id}
              tag={tag as Tag}
              onToggle={() => handleToggleAdd(tag as Tag)}
              onEdit={() => {
                setEditingTagId(tag.id ?? null);
                setCreating(false);
              }}
              onDelete={() => handleDelete(tag as Tag)}
            />
          ),
        )}
        {isFetching && (
          <div className="flex justify-center py-1.5">
            <Skeleton className="h-3 w-16 rounded" />
          </div>
        )}
      </div>

      {!creating ? (
        <button
          type="button"
          data-testid="tasks-tag-picker-create-btn"
          className="flex w-full items-center gap-1.5 border-t px-3 py-2 text-xs text-primary hover:bg-accent"
          onClick={() => {
            setCreating(true);
            setEditingTagId(null);
          }}
        >
          <Plus className="size-3" />
          {t('tagsInline.createNew')}
        </button>
      ) : (
        <TagCreateForm onSave={handleCreate} onCancel={() => setCreating(false)} />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(v) => {
          if (!v) handleCancel();
        }}
        title={confirmOptions?.title ?? ''}
        description={confirmOptions?.description}
        confirmLabel={confirmOptions?.confirmLabel}
        cancelLabel={confirmOptions?.cancelLabel}
        variant={confirmOptions?.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </PopoverContent>
  );

  if (variant === 'compact') {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type="button"
            ref={triggerRef}
            data-testid="tasks-tag-picker"
            className="flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:bg-accent"
          >
            {selectedTags.length === 0 ? (
              <span className="text-xs text-muted-foreground">—</span>
            ) : (
              <>
                {selectedTags.slice(0, 2).map((tag) => (
                  <TagBadge key={tag.id} tag={tag} />
                ))}
                {selectedTags.length > 2 && (
                  <span className="text-xs text-muted-foreground">+{selectedTags.length - 2}</span>
                )}
              </>
            )}
          </button>
        </PopoverTrigger>
        {popoverContent}
      </Popover>
    );
  }

  return (
    <div data-testid="tasks-tag-picker" className="flex flex-wrap items-center gap-1">
      {selectedTags.map((tag) => (
        <TagBadge key={tag.id} tag={tag} />
      ))}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type="button"
            ref={triggerRef}
            data-testid="tasks-tag-picker-add-btn"
            className="flex items-center gap-1 rounded-md border border-dashed px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-secondary-foreground"
          >
            <Plus className="size-3" />
            {t('tagsInline.addAria')}
          </button>
        </PopoverTrigger>
        {popoverContent}
      </Popover>
    </div>
  );
}
